import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import { useRef } from 'react';
import { Control, Controller, FieldErrors, FieldValues } from 'react-hook-form';
import { isValid } from 'date-fns';
import { clearSeconds } from '@dispatch/shared';
import { toPickerDate } from './pickerValue';
import { useDateTimeDialog } from './useDateTimeDialog';
import type { DateTimeDialog } from './useDateTimeDialog';

// v5's default 12-hour format; v6 defaults to an uppercase meridiem.
const DATE_TIME_FORMAT = 'P hh:mm aaa';

const REQUIRED = { required: true };
const ACTION_BAR = { actions: ['cancel' as const, 'accept' as const] };
// v8 mobile pickers pick the time on digital lists; v7 used the analog clock.
const VIEW_RENDERERS = {
  hours: renderTimeViewClock,
  minutes: renderTimeViewClock,
  seconds: renderTimeViewClock,
};

type DateTimeFieldProps = {
  id: string;
  name: string;
  label: string;
  control: Control<FieldValues>;
  onChange?: (values: string) => void;
  errors?: FieldErrors;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
};

type DialogPickerProps = Omit<DateTimeFieldProps, 'name' | 'control'> & {
  value: string;
  onValueChange: (value: string) => void;
};

function toMinuteIso(date: Date | null): string | undefined {
  if (!date || !isValid(date)) return undefined;
  const [withoutSeconds] = clearSeconds(date);
  return withoutSeconds.toISOString();
}

function pickerSlotProps(
  { id, errors, required, readOnly }: DialogPickerProps,
  dialog: DateTimeDialog
) {
  return {
    field: { readOnly: readOnly ?? true, ...dialog.fieldHandlers },
    actionBar: ACTION_BAR,
    textField: {
      focused: dialog.open ? false : undefined,
      sx: { width: '100%' },
      id,
      variant: 'standard' as const,
      error: !!errors && !!errors[id]?.message,
      helperText: !!errors && errors[id]?.message,
      required,
    },
  };
}

function DateTimeDialogPicker(props: DialogPickerProps) {
  const { label, onChange, readOnly, disabled, value, onValueChange } = props;
  const now = useRef(new Date()).current;
  const dialog = useDateTimeDialog(!(disabled || readOnly));
  const localeText = { toolbarTitle: label };
  return (
    <MobileDateTimePicker
      enableAccessibleFieldDOMStructure={false}
      disableOpenPicker
      open={dialog.open}
      onClose={dialog.close}
      view={dialog.view}
      viewRenderers={VIEW_RENDERERS}
      onViewChange={dialog.changeView}
      onChange={(date) => {
        const iso = toMinuteIso(date);
        if (iso) onValueChange(iso);
        dialog.moveToClockAfterDay();
      }}
      onAccept={(date) => {
        const iso = toMinuteIso(date);
        if (iso) onChange?.(iso);
      }}
      slotProps={pickerSlotProps(props, dialog)}
      format={DATE_TIME_FORMAT}
      label={label}
      localeText={localeText}
      ampmInClock
      value={toPickerDate(new Date(value), now)}
      readOnly={readOnly}
      disabled={disabled}
    />
  );
}

export const DateTimeField = function ({
  name,
  control,
  ...pickerProps
}: DateTimeFieldProps) {
  return (
    <Controller
      rules={REQUIRED}
      control={control}
      name={name}
      render={({ field: { onChange: onValueChange, value } }) => (
        <DateTimeDialogPicker
          {...pickerProps}
          value={value}
          onValueChange={onValueChange}
        />
      )}
    />
  );
};
